import DomHelper from '../../Core/helper/DomHelper.js';
import InstancePlugin from '../../Core/mixin/InstancePlugin.js';
import GridFeatureManager from './GridFeatureManager.js';
import SummaryFormatter from './mixin/SummaryFormatter.js';

/**
 * @module Grid/feature/Summary
 */

/**
 * @typedef {Object} ColumnSummaryConfig
 * @property {'sum'|'add'|'count'|'countNotEmpty'|'average'|Function} [sum] Summary type, see
 *   {@link Grid/column/Column#config-sum} for details
 * @property {Function} [renderer] Renderer function for summary, see
 *   {@link Grid/column/Column#config-summaryRenderer} for details
 * @property {*} [seed] Initial value when using a function as `sum`
 */

/**
 * Displays a summary row in the grid footer.
 *
 * {@inlineexample Grid/feature/Summary.js}
 *
 * On each column you can specify the type of {@link Grid/column/Column#config-sum}, available types are:
 * <dl class="wide">
 * <dt>sum <dd>Sum of all values in the column
 * <dt>add <dd>Alias for sum
 * <dt>count <dd>Number of rows
 * <dt>countNotEmpty <dd>Number of rows containing a value
 * <dt>average <dd>Average of all values in the column
 * <dt>function <dd>A custom function, used with store.reduce. Should take arguments (sum, record)
 * </dl>
 * Columns can also specify a {@link Grid/column/Column#config-summaryRenderer} to format the calculated sum.
 *
 * This feature is <strong>disabled</strong> by default.
 *
 * ```javascript
 * { text : 'Score', data : 'score', width : 80, sum : 'sum' }
 * { text : 'Rank', data : 'rank', width : 80, sum : 'average', summaryRenderer: ({ sum }) => return 'Average rank ' + sum }
 * ```
 *
 * Also, it is possible to set up multiple summaries as array of summary configs:
 * ```javascript
 * { text : 'Rank', data : 'rank', summaries : [{ sum : 'average', label : 'Average' }, { sum : 'count', label : 'Count' }] }
 * ```
 *
 * {@note}When Store has {@link Core.data.Store#config-lazyLoad}, this feature will only work on locally available data{/@note}
 *
 * @extends Core/mixin/InstancePlugin
 * @demo Grid/features
 * @classtype summary
 * @feature
 */
export default class Summary extends SummaryFormatter(InstancePlugin) {
    //region Config
    static get configurable() {
        return {
            /**
             * Set to `true` to sum values of selected row records
             * @prp {Boolean}
             * @default false
             */
            selectedOnly : null,

            /**
             * Set to `true` to only refresh columns with fields matching the changed fields after a field update
             * @prp {Boolean}
             * @default false
             */
            refreshOnlyAffectedSummaries : null,

            hideFooters : false
        };
    }

    // Plugin configuration. This plugin chains some of the functions in Grid.
    static get pluginConfig() {
        return {
            chain : ['renderRows', 'bindStore']
        };
    }

    //endregion

    //region Init

    static $name = 'Summary';

    construct(grid, config) {
        this.grid = grid;

        super.construct(grid, config);

        this.bindStore(grid.store);

        grid.hideFooters = this.hideFooters;
    }

    bindStore(store) {
        this.detachListeners('store');

        store.ion({
            name    : 'store',
            change  : 'onStoreChange',
            thisObj : this
        });

        if (!this.isConfiguring) {
            this.refreshSummaries();
        }
    }

    get store() {
        return this.grid.store;
    }

    doDestroy() {
        super.doDestroy();
    }

    doDisable(disable) {
        super.doDisable(disable);

        const { client } = this;

        if (disable) {
            client.element.classList.add('b-summary-disabled');
        }
        else {
            this.refreshSummaries();
            client.element.classList.remove('b-summary-disabled');
            client.eachSubGrid(subGrid => subGrid.scrollable.syncPartners());
        }
    }

    //endregion

    //region Render

    renderRows() {
        this.refreshSummaries();
    }

    /**
     * Updates summaries. Summaries are displayed as tables in footer (styling left out to keep brief):
     * ```
     * <table>
     *     <tr><td colspan="2">0</td></tr> // { sum : 'min' } Only a calculation, span entire table
     *     <tr><td>Max</td><td>10</td></tr> // { sum : 'max', label: 'Max' } Label + calculation
     *     <tr><td>Max</td><td>10</td></tr> // { sum : 'sum', label: 'Max' } Label + calculation
     * </table>
     * ```
     * @private
     */
    refreshSummaries(fields) {
        const
            me              = this,
            { grid, store } = me,
            cells           = fields ? null : DomHelper.children(grid.element, '.b-grid-footer'),
            selectedOnly    = me.selectedOnly && grid.selectedRecords.length > 0,
            records         = (store.isFiltered ? store.storage.values : store.allRecords).filter(r => !r.isSpecialRow && (!selectedOnly || grid.isSelected(r))),
            columns         = fields ? fields.map(field => grid.columns.get(field)) : grid.columns.visibleColumns;
        // reset seeds, to not have ever-increasing sums :)
        columns.forEach((column, i) => {
            column.summaries?.forEach(config => {
                if ('seed' in config) {
                    if (!('initialSeed' in config)) {
                        config.initialSeed = config.seed;
                    }

                    if (['number', 'string', 'date'].includes(typeof config.initialSeed)) {
                        config.seed = config.initialSeed;
                    }
                    else {
                        // create shallow copy
                        config.seed = Object.assign({}, config.initialSeed);
                    }
                }
            });

            const cellElement = fields ? grid.element.querySelector(`.b-grid-footer[data-column-id=${column.id}]`) : cells[i];

            // Skip for special columns like checkbox selection
            if (!cellElement?.dataset.column) {
                return;
            }

            const
                html   = me.generateHtml(column, records, 'b-grid-footer-summary');

            if (column.summaries ? column.summaries.length : column.sum ? 1 : 0) {
                // First time, set table
                if (!cellElement.children.length) {
                    cellElement.innerHTML = html;
                }
                // Following times, sync changes
                else {
                    DomHelper.sync(html, cellElement.firstElementChild);
                }
            }
        });
    }

    //endregion

    //region Events

    /**
     * Updates summaries on store changes (except record update, handled below)
     * @private
     */
    onStoreChange({ action, changes }) {
        let shouldUpdate = true;

        if (this.disabled) {
            return;
        }

        if (action === 'update') {
            shouldUpdate = Object.keys(changes).some(field => {
                const colField = this.grid.columns.get(field);
                // check existence, since a field not used in a column might have changed
                return Boolean(colField) && (Boolean(colField.sum) || Boolean(colField.summaries));
            });
        }

        if (shouldUpdate) {
            this.refreshSummaries(action === 'update' && this.refreshOnlyAffectedSummaries ? Object.keys(changes) : undefined);
        }
    }

    //endregion

    updateSelectedOnly(value) {
        const me = this;
        me.detachListeners('selectionChange');

        if (value) {
            me.grid.ion({
                name            : 'selectionChange',
                selectionChange : me.refresh,
                thisObj         : me
            });
        }
        me.refresh();
    }

    /**
     * Refreshes the summaries
     */
    refresh() {
        this.refreshSummaries();
    }
}

Summary.featureClass = 'b-summary';

GridFeatureManager.registerFeature(Summary);
